import { identity } from "@suse-o11y/identity";
import { ImmutableSet } from "@suse-o11y/immutable-set";
import { isDeepEqual } from "@suse-o11y/is-deep-equal";
import { type UseMutationOptions, useMutation, useQueryClient } from "@suse-o11y/query";
import { useHandler } from "@suse-o11y/use-handler";
import React from "react";

import { ErrorMessage } from "../components/_notification-error";
import { DONT_AUTO_DISMISS, notification } from "../components/react/notification/Notification";
import { type Setter, resolve } from "../generic/setter";
import { type OptimisticUpdate, optimisticQueryUpdate, useQuery } from "../queries/react-query";
import { mutateUserProfile, queryUserProfile } from "../queries/user-profile";

import type { UserProfile as UserProfileOpenAPI } from "./openapi";

export interface UserProfile
	extends Omit<UserProfileOpenAPI, "systemNotificationsRead" | "starredViews" | "starredDashboards"> {
	systemNotificationsRead: ImmutableSet<string>;
	starredViews: ImmutableSet<number>;
	starredDashboards: ImmutableSet<number>;
}

const userProfileFromOpenApi = (userProfile: UserProfileOpenAPI): UserProfile => ({
	...userProfile,
	systemNotificationsRead: ImmutableSet.fromArray(userProfile.systemNotificationsRead),
	starredViews: ImmutableSet.fromArray(userProfile.starredViews),
	starredDashboards: ImmutableSet.fromArray(userProfile.starredDashboards),
});

const userProfileToOpenApi = (userProfile: UserProfile): UserProfileOpenAPI => ({
	...userProfile,
	systemNotificationsRead: userProfile.systemNotificationsRead.toArray(),
	starredViews: userProfile.starredViews.toArray(),
	starredDashboards: userProfile.starredDashboards.toArray(),
});

type UseUserProfileOptions = UseMutationOptions<
	UserProfileOpenAPI,
	Error,
	UserProfileOpenAPI,
	{ profile: undefined | OptimisticUpdate<UserProfileOpenAPI> }
>;

export interface UseUserProfileOptionsWithSelect<TData> extends UseUserProfileOptions {
	select: (userProfile: UserProfile) => TData;
}

export type UseUserProfileResult<TData> = [
	userProfile: undefined | TData,
	updateUserProfile: React.Dispatch<Setter<UserProfile>>,
];

export function useUserProfile<TData = UserProfile>({
	onMutate,
	onSuccess,
	onError,
	select = identity as (userProfile: UserProfile) => TData,
	...options
}: Partial<UseUserProfileOptionsWithSelect<TData>> = {}): UseUserProfileResult<TData> {
	const queryClient = useQueryClient();

	const { data: userProfile } = useQuery({
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		...queryUserProfile,
		select: userProfileFromOpenApi,
	});

	const { mutate: update } = useMutation({
		...mutateUserProfile,
		...options,
		onMutate: async (newUserProfile) => {
			const profile = optimisticQueryUpdate(queryClient, queryUserProfile, newUserProfile);

			await Promise.all([profile?.cancelOngoingQuery(), onMutate?.(newUserProfile)]);

			return { profile };
		},
		onSuccess: (data, variables, context) => {
			queryUserProfile.setData(queryClient, data);

			return onSuccess?.(data, variables, context);
		},
		onError: (error, variables, context) => {
			context?.profile?.rollbackOptimisticUpdates();

			if (onError != null) {
				return onError(error, variables, context);
			}

			notification.error({
				duration: DONT_AUTO_DISMISS,
				message: <ErrorMessage error={error}>Something went wrong while trying to save user profile</ErrorMessage>,
			});
		},
	});

	const updateUserProfile = useHandler((setter: Setter<UserProfile>) => {
		const nextUserProfile = resolve(setter, userProfile);

		if (!isDeepEqual(userProfile, nextUserProfile)) {
			update(userProfileToOpenApi(nextUserProfile));
		}
	});

	const selected = React.useMemo(() => (userProfile == null ? undefined : select(userProfile)), [userProfile, select]);

	return [selected, updateUserProfile];
}
